import {
  ContentCotainer,
  ContentLoadingState,
} from '../../shared/ui/layouts/PageContent';
import { useTransfers } from './useTransfers';
import { EmptyTransfers } from './EmptyTransfers';
import { Table } from 'shared/ui/Table/TableBlocks';
import { useMemo, useState } from 'react';
import { AddNewTransferModal } from './AddNewTransferModal';
import { extractLastChar, formatDollarAmount } from '../../shared/lib/string';
import dayjs from 'dayjs';
import { PaymentDetailsTypes } from '../payments/paymentDetails/PaymentDetailsTypes';
import { Badge } from 'flowbite-react';
import { Flex } from 'antd';
import { US, EU } from 'country-flag-icons/react/1x1';
import {
  isCryptoCurrency,
  NetworkLogoMap,
  upperCaseNetworkType,
  getTokenHumanReadableNameByChain,
} from 'features/bill/lib';
import { CreditCardIcon } from '@heroicons/react/24/outline';
import { getTransfersPaymentProvidersEvaluatorStrategy } from '../payments/getPaymentProvidersEvaluatorStrategy';
import { createPlatformIntegrations } from '../platformIntegrations/PlatformIntegration';
import { useCurrentUserDetails } from '../../entities/user/model';
import { PaymentProviderSelection } from '../payments/PaymentProviderSelection';
import { PaymentProviderIdTypes } from '../payments/PaymentProviderIdTypes';
import { FiatCryptoTransfersPaymentModal } from '../payments/paymentModals/PaysoOnrampPaymentModal';
import { TransferPaymentViaCrossChainTransferModal } from '../payments/paymentModals/CrossChainTransferModal';
import { TransferOverviewSlideOver } from './TransferOverviewSlideOver';
import { createOrgSettingsFactory } from '../organization/orgSettings/OrgSettings';
import { TransferPaymentViaOfframpModal } from '../payments/paymentModals/OfframpModal';
import { PaymentsOnboardingWidget } from '../payments/paymentModals/PaymentsOnboardingModal';
import { InitiateTransferWidget } from './InitiateTransferWidget/InitiateTransferWidget';

export const TransferStatusBadge = ({ transfer }) => {
  if (['success'].includes(transfer?.payablePaymentStatus)) {
    return (
      <Badge className="inline !bg-slate-700 !text-emerald-400">Paid</Badge>
    );
  }

  if (
    ['executed', 'awaiting_execution'].includes(transfer?.payablePaymentStatus)
  ) {
    return (
      <Badge className="inline !bg-slate-700 !text-indigo-300">
        Processing
      </Badge>
    );
  }

  return <Badge className="inline !bg-slate-700 !text-slate-200">Unpaid</Badge>;
};

export const TransfersPage = () => {
  const { getOrgIntegrations, getOrgSettings } = useCurrentUserDetails();
  const [transfersToPay, setTransfersToPay] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const { transfers, isFetching, isPending } = useTransfers();
  const [transferCandidate, setTransferCandidate] = useState(null);
  const [transferToOverview, setTransferToOverview] = useState(null);

  const evaluator = getTransfersPaymentProvidersEvaluatorStrategy(
    transfersToPay.length
      ? transfersToPay
      : Object.keys(rowSelection).map((idx) => transfers[idx]),
    createPlatformIntegrations(getOrgIntegrations()),
    createOrgSettingsFactory(getOrgSettings())
  );

  const renderCurrencyImage = (c, n) => {
    let src;
    if (isCryptoCurrency(c)) {
      src = NetworkLogoMap[upperCaseNetworkType(n)];
    } else if (c === 'USD') {
      return <US className="w-4 rounded-full" />;
    } else if (c === 'EUR') {
      return <EU className="w-4 rounded-full" />;
    }

    return (
      <div className="h-4 w-4">
        <img src={src} alt="" aria-hidden="true" />
      </div>
    );
  };

  const renderCurrencyLabel = (c, n) => {
    let label;
    if (isCryptoCurrency(c)) {
      label = (
        <span className="flex items-center">
          {getTokenHumanReadableNameByChain(c, n)}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-3">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m9 20.247 6-16.5"
            />
          </svg>
          {upperCaseNetworkType(n)}
        </span>
      );
    } else {
      label = c;
    }

    return <span>{label}</span>;
  };

  const columns = useMemo(() => {
    const res = [
      {
        header: 'selection',
      },

      {
        header: 'From',
        cell: ({ row: { original: item } }) => {
          if (item.payablePaymentMeta?.methodPaymentDetails) {
            throw new Error('Render is missing');
          }
          const sourceCurrency = item.sourceCurrency ? (
            <Flex align="center" gap={'small'}>
              {renderCurrencyImage(item.sourceCurrency, item.sourceNetwork)}
              {renderCurrencyLabel(item.sourceCurrency, item.sourceNetwork)}
            </Flex>
          ) : null;
          let sourcePaymentDetails = 'External Bank';

          if (
            item.sourceNetwork !== 'fiat' &&
            item.destinationNetwork !== 'fiat'
          ) {
            sourcePaymentDetails = 'External Wallet';
          }

          if (item.destinationNetwork === 'fiat') {
            sourcePaymentDetails = 'External Wallet';
          }

          return (
            <Flex align="center" gap={'small'}>
              {sourceCurrency}
              <span className="text-slate-500 text-xs">
                {sourcePaymentDetails}
              </span>
            </Flex>
          );
        },
      },
      {
        header: 'To',
        cell: ({ row: { original: item } }) => {
          const destCurrency = (
            <Flex align="center" gap={'small'}>
              {renderCurrencyImage(
                item.destinationCurrency,
                item.destinationNetwork
              )}
              {renderCurrencyLabel(
                item.destinationCurrency,
                item.destinationNetwork
              )}
            </Flex>
          );

          if (
            item.destinationPaymentDetails.type ===
            PaymentDetailsTypes.CryptoWallet
          ) {
            return (
              <Flex align="center" gap={'small'}>
                {destCurrency}
                <span className="text-slate-500 text-xs">
                  ***
                  {extractLastChar(item.destinationPaymentDetails.address, 6)}
                  {item.destinationPaymentDetailsMeta?.pdsName &&
                    ` ${item.destinationPaymentDetailsMeta?.pdsName}`}
                </span>
              </Flex>
            );
          }

          if (
            item.destinationPaymentDetails.type ===
            PaymentDetailsTypes.BankAccount
          ) {
            return (
              <Flex align="center" gap={'small'}>
                {destCurrency}
                <span className="text-slate-500 text-xs">
                  ***
                  {extractLastChar(
                    item.destinationPaymentDetails.accountIBAN ||
                      item.destinationPaymentDetails.accountNumber,
                    4
                  )}{' '}
                  {item.destinationPaymentDetails.accountBeneficiary}
                </span>
              </Flex>
            );
          }

          return 'Bank Account';
        },
      },
      {
        header: 'Amount',
        cell: ({ row: { original: item } }) => (
          <>{formatDollarAmount(item.transferAmount)}</>
        ),
      },
      {
        header: 'Date',
        cell: ({ row: { original: item } }) => {
          return dayjs(item.createdAt).format('DD MMM YY');
        },
      },
      {
        header: 'Status',
        cell: ({ row: { original: item } }) => (
          <TransferStatusBadge transfer={item} />
        ),
      },
    ];

    return res;
  }, []);

  const _handleCloseNewTransferModal = () => {
    setTransferCandidate(null);
  };

  const _handleRowClick = (row) => {
    setTransferToOverview(row);
  };

  const _formSecondaryActionList = () => {
    const res = [];

    if (evaluator?.getAllPossiblePaymentProviders().length > 0) {
      res.push({
        label: 'Complete Transfer',
        icon: () => <CreditCardIcon className="w-6 mr-2" />,
        onClick: () => {
          setTransfersToPay(
            Object.keys(rowSelection).map((idx) => transfers[idx])
          );
        },
      });
    }

    return res;
  };

  const _handleRowSelectionChange = (getUpdate) => {
    if (transfersToPay.length) {
      setTransfersToPay([]);
    }
    setRowSelection(getUpdate(rowSelection));
  };

  const _handleInitiateTransfer = (candidate) => {
    setTransferCandidate(candidate);
  };

  if (isPending) {
    return <ContentLoadingState />;
  }

  return (
    <ContentCotainer className={'max-w-7xl'}>
      <PaymentsOnboardingWidget />
      <div className="mb-6 flex justify-center">
        <InitiateTransferWidget onInitiateTransfer={_handleInitiateTransfer} />
      </div>
      {transfers.length === 0 && !isFetching ? (
        <EmptyTransfers />
      ) : (
        <Table
          title="Transfer History"
          description="Buy & sell stablecoins by creating a new transfer."
          onRowClick={_handleRowClick}
          secondaryActionList={_formSecondaryActionList()}
          columns={columns}
          data={transfers}
          isDataLoading={isFetching}
          rowSelection={rowSelection}
          onRowSelectionChange={_handleRowSelectionChange}
        />
      )}
      <PaymentsModalManager
        transfers={transfersToPay}
        paymentProvidersEvaluator={evaluator}
        onCancelPayment={() => {
          setTransfersToPay([]);
        }}
      />

      <AddNewTransferModal
        transferCandidate={transferCandidate}
        onTransferCreated={(tr) => {
          setTransfersToPay([tr]);
        }}
        show={!!transferCandidate}
        onClose={_handleCloseNewTransferModal}
      />

      <TransferOverviewSlideOver
        onCompleteTransferClick={() => {
          setTransfersToPay([transferToOverview]);
        }}
        transfer={transferToOverview}
        onClose={() => {
          setTransferToOverview(null);
        }}
      />
    </ContentCotainer>
  );
};

const PaymentsModalManager = ({
  transfers,
  paymentProvidersEvaluator,
  onCancelPayment,
}) => {
  const [paymentProvider, setPaymentProvider] = useState(null);

  const _handleSelectPaymentProvider = (provider) => {
    setPaymentProvider(provider);
  };

  const _handleClose = () => {
    onCancelPayment();
    setPaymentProvider(null);
  };

  if (!transfers.length) {
    return null;
  }

  const renderModal = () => {
    if (
      ['easy-pay-onramp', 'red-envelope-onramp', 'bridge-onramp'].includes(
        paymentProvider?.id
      )
    ) {
      return (
        <FiatCryptoTransfersPaymentModal
          transfers={transfers}
          payouts={paymentProvidersEvaluator?.getPayouts()}
          onClose={_handleClose}
          paymentProviderConfig={paymentProvider}
        />
      );
    }

    if (
      [PaymentProviderIdTypes.EasyPayCrossChainTransfer].includes(
        paymentProvider?.id
      )
    ) {
      return (
        <TransferPaymentViaCrossChainTransferModal
          onClose={_handleClose}
          show={true}
          transfers={transfers}
          payouts={paymentProvidersEvaluator?.getPayouts()}
        />
      );
    }

    if (
      [
        PaymentProviderIdTypes.BridgeOfframp,
        PaymentProviderIdTypes.EasyPayOfframpTransfer,
      ].includes(paymentProvider?.id)
    ) {
      return (
        <TransferPaymentViaOfframpModal
          transfers={transfers}
          onClose={_handleClose}
          show={true}
          payouts={paymentProvidersEvaluator?.getPayouts()}
          paymentProviderConfig={paymentProvider}
        />
      );
    }

    return null;
  };

  return (
    <>
      <PaymentProviderSelection
        title="Select Payment Method"
        show={!paymentProvider}
        onSelectProvider={_handleSelectPaymentProvider}
        paymentProvidersEvaluator={paymentProvidersEvaluator}
        onClose={_handleClose}
      />

      {renderModal()}
    </>
  );
};
